import { useCallback } from 'react';
import { useAuth } from '../../zustand';

export const useAccessControl = () => {
  const { accessControl } = useAuth((s) => s);

  const normalizeName = useCallback((value) => {
    return String(value || '')
      .trim()
      .toLowerCase();
  }, []);

  const isGroupNode = useCallback((node) => {
    const hasSubItems = (node?.['sub-item'] || [])?.length > 0;
    if (!hasSubItems) return false;

    const hasSubTabs = (node?.['sub-tab'] || [])?.length > 0;
    const hasGlobalActions = Array.isArray(node?.['global-action']);
    const hasItemActions = Array.isArray(node?.['item-action']);

    return !hasSubTabs && !hasGlobalActions && !hasItemActions;
  }, []);

  const findTopLevel = useCallback(
    (page) => {
      const needle = normalizeName(page);
      const match = accessControl?.find(
        (val) => normalizeName(val?.name) === needle,
      );
      return match && !isGroupNode(match) ? match : undefined;
    },
    [accessControl, normalizeName, isGroupNode],
  );

  const findNested = useCallback(
    (page) => {
      const needle = normalizeName(page);
      if (!accessControl?.length) return undefined;

      const stack = [...accessControl];
      while (stack.length) {
        const node = stack.shift();
        if (!node) continue;

        if (normalizeName(node?.name) === needle && !isGroupNode(node)) {
          return node;
        }

        const children = [
          ...(node?.['sub-item'] || []),
          ...(node?.['sub-tab'] || []),
        ];
        if (children?.length) stack.unshift(...children);
      }

      return undefined;
    },
    [accessControl, normalizeName, isGroupNode],
  );

  const findPage = useCallback(
    (page) => {
      return findTopLevel(page) || findNested(page);
    },
    [findNested, findTopLevel],
  );

  const findChild = useCallback(
    ({ page, subPage }) => {
      const parent = findPage(page);
      if (!parent) return undefined;

      const needle = normalizeName(subPage);
      const children = [
        ...(parent?.['sub-item'] || []),
        ...(parent?.['sub-tab'] || []),
      ];

      return children.find((child) => normalizeName(child?.name) === needle);
    },
    [findPage, normalizeName],
  );

  const hasGlobalAccess = useCallback(
    ({ page, subPage = '', keys, subSidebar = false }) => {
      if (subSidebar) {
        return findChild({ page, subPage })?.['global-action']?.includes(keys);
      } else {
        return findPage(page)?.['global-action']?.includes(keys);
      }
    },
    [findChild, findPage],
  );

  const hasActionAccess = useCallback(
    ({ page, subPage = '', keys, subSidebar = false }) => {
      if (subSidebar) {
        return findChild({ page, subPage })?.['item-action']?.includes(keys);
      } else {
        return findPage(page)?.['item-action']?.includes(keys);
      }
    },
    [findChild, findPage],
  );

  const hasAction = useCallback(
    ({ page, subPage = '', subSidebar = false }) => {
      if (subSidebar) {
        return (
          (findChild({ page, subPage })?.['item-action']?.length || 0) > 0
        );
      } else {
        return (
          (findPage(page)?.['item-action']?.length || 0) > 0
        );
      }
    },
    [findChild, findPage],
  );

  return { hasGlobalAccess, hasActionAccess, hasAction };
};
